import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Microphone, Stop, X, CheckCircle } from "@phosphor-icons/react";

/**
 * WO Voice-Note — big red mic button on the Show-Up Card.
 * Tap → record → tap again to stop → Whisper transcribes → Claude drafts
 * both a tenant-facing update AND a crew-facing internal note. Tech reviews
 * before it goes anywhere.
 */
export default function WOVoiceNoteButton({ jobId, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = e => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach(t => t.stop());
        if (blob.size < 500) {
          toast.error("Too short — try again");
          return;
        }
        setBusy(true);
        const tid = toast.loading("Transcribing + drafting…");
        try {
          const fd = new FormData();
          fd.append("file", blob, "note.webm");
          const r = await api.post(`/jobs/${jobId}/voice-note`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.dismiss(tid);
          setResult(r.data);
        } catch (e) {
          toast.dismiss(tid);
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Voice-note failed");
        } finally { setBusy(false); }
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Mic permission denied — enable in browser settings");
    }
  };
  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  return (
    <>
      <button
        onClick={recording ? stop : start}
        disabled={busy}
        data-testid="wo-voice-note-btn"
        className={`h-12 px-4 border-2 border-[#0f172a] font-display font-black text-sm uppercase tracking-widest shadow-[3px_3px_0_#0f172a] transition-transform hover:-translate-y-0.5 flex items-center gap-2 ${
          recording
            ? "bg-red-600 text-white animate-pulse"
            : "bg-slate-900 text-white hover:bg-slate-800"
        }`}>
        {recording ? <><Stop weight="fill" size={16}/> STOP</> : <><Microphone weight="fill" size={16}/> VOICE NOTE</>}
      </button>
      {result && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="voice-note-review" onClick={() => { setResult(null); onSaved?.(); }}>
          <div className="bg-white border-4 border-[#0f172a] max-w-lg w-full my-8 shadow-[8px_8px_0_#000]"
            onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-slate-900 flex items-center justify-between bg-cyan-100">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Voice note saved</div>
                <div className="font-display font-black text-lg">AI drafted 2 versions</div>
              </div>
              <button onClick={() => { setResult(null); onSaved?.(); }}
                className="w-10 h-10 border-2 border-slate-900 bg-white hover:bg-red-100 flex items-center justify-center">
                <X weight="bold" size={18}/>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">Tenant-facing update</div>
                <div className="p-3 border-2 border-emerald-500 bg-emerald-50 text-sm whitespace-pre-wrap">
                  {result.customer_update || <span className="italic text-slate-400">—</span>}
                </div>
                {result.customer_update && (
                  <button onClick={() => { navigator.clipboard.writeText(result.customer_update); toast.success("Copied for text"); }}
                    className="mt-2 h-8 px-3 border-2 border-emerald-700 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                    <CheckCircle weight="bold" size={12}/> Copy for text
                  </button>
                )}
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-700 mb-1">Internal note (crew only)</div>
                <div className="p-3 border-2 border-slate-400 bg-slate-50 text-sm whitespace-pre-wrap font-mono">
                  {result.internal_note || <span className="italic text-slate-400">—</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Raw transcript</div>
                <div className="p-2 bg-white border border-slate-200 text-xs text-slate-600 italic">{result.transcript}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
